"use client";

import { useState, useEffect, useRef } from "react";
import { signIn, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";

type Gender = "female" | "male";

const PERSONAS = {
  female: {
    name: "Priya",
    tagline: "Minimalist. Bold. Effortless.",
    loadingText: "Preparing Priya's wardrobe...",
    casualImg: "/demo/priya-casual.png",
    styledImg: "/demo/priya-styled.png",
  },
  male: {
    name: "Aryan",
    tagline: "Sharp. Relaxed. Always on point.",
    loadingText: "Preparing Aryan's wardrobe...",
    casualImg: "/demo/aryan-casual.png",
    styledImg: "/demo/aryan-styled.png",
  },
} as const;

export default function DemoPage() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const mainRef = useRef<HTMLElement>(null);
  const [selected, setSelected] = useState<Gender | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredCard, setHoveredCard] = useState<Gender | null>(null);
  const [autoCycleCard, setAutoCycleCard] = useState<Gender | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile (no hover support)
  useEffect(() => {
    const mq = window.matchMedia("(hover: none)");
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // Auto-cycle styled look on mobile — alternate cards every 3s
  useEffect(() => {
    if (!isMobile || loading) return;
    let index = 0;
    let intervalId: ReturnType<typeof setInterval>;
    const genders: Gender[] = ["female", "male"];

    // Start after a short delay so the page settles
    const startTimer = setTimeout(() => {
      setAutoCycleCard(genders[0]);
      intervalId = setInterval(() => {
        index = (index + 1) % genders.length;
        setAutoCycleCard(genders[index]);
      }, 3000);
    }, 1500);

    return () => {
      clearTimeout(startTimer);
      clearInterval(intervalId);
      setAutoCycleCard(null);
    };
  }, [isMobile, loading]);

  // Dynamically match the .app-frame container height (932px on desktop, 100svh on mobile)
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const frame = el.closest(".app-frame") as HTMLElement | null;
    if (frame) {
      const sync = () => { el.style.height = `${frame.clientHeight}px`; };
      sync();
      const ro = new ResizeObserver(sync);
      ro.observe(frame);
      return () => ro.disconnect();
    }
  }, [status]);

  const handleSelect = async (gender: Gender) => {
    if (loading) return;
    setSelected(gender);
    setLoading(true);
    setError(null);
    try {
      const result = await signIn("demo", {
        gender,
        callbackUrl: "/user/dashboard",
        redirect: false,
      });
      if (result?.error) {
        setError("Demo unavailable right now. Try again.");
        setLoading(false);
        setSelected(null);
      } else if (result?.url) {
        router.push(result.url);
      }
    } catch {
      setError("Something went wrong. Try again.");
      setLoading(false);
      setSelected(null);
    }
  };

  if (status === "loading") return null;

  return (
    <main ref={mainRef} className="flex w-full flex-col overflow-hidden bg-[#fffdfa]" style={{ height: "100dvh" }}>
      <style>{`
        @keyframes goldShimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4">
        <img src="/logo.png" alt="Hey Hoku" className="h-9" />
        <a
          href="/login?action=signup"
          className="font-winky text-[13px] text-[#2d2d2d]/60 underline underline-offset-2"
        >
          Sign up free
        </a>
      </div>

      {/* Headline */}
      <div className="px-5 pb-4 pt-2">
        <p className="font-winky text-[22px] font-bold text-[#2d2d2d] leading-tight">
          Whose wardrobe do you want to explore?
        </p>
      </div>

      {/* Cards */}
      <div className="flex flex-1 min-h-0 flex-col gap-3 px-5 pb-6">
        {(["female", "male"] as Gender[]).map((gender) => {
          const persona = PERSONAS[gender];
          const isSelected = selected === gender;
          const isOther = selected !== null && selected !== gender;
          const isHovered = isMobile ? autoCycleCard === gender : hoveredCard === gender;

          return (
            <button
              key={gender}
              role="button"
              aria-label={`Explore as ${persona.name}`}
              tabIndex={0}
              onClick={() => handleSelect(gender)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleSelect(gender);
              }}
              onMouseEnter={() => !isMobile && setHoveredCard(gender)}
              onMouseLeave={() => !isMobile && setHoveredCard(null)}
              disabled={loading}
              className={[
                "relative flex flex-1 min-h-0 flex-col justify-end overflow-hidden rounded-2xl transition-all duration-300",
                "bg-[#f0ead8] border-2 border-[#e0d9c0]",
                isSelected
                  ? "outline outline-3 outline-[#ffd372] border-[#ffd372]"
                  : "outline-none",
                isOther ? "opacity-50" : "opacity-100",
                "disabled:cursor-not-allowed",
              ].join(" ")}
            >
              {/* Casual image (default) */}
              <img
                src={persona.casualImg}
                alt={`${persona.name} casual`}
                className="absolute inset-0 w-full h-full object-contain object-bottom transition-opacity duration-500"
                style={{ opacity: isHovered ? 0 : 1 }}
                draggable={false}
              />

              {/* Styled image (on hover) */}
              <img
                src={persona.styledImg}
                alt={`${persona.name} styled by Hoku`}
                className="absolute inset-0 w-full h-full object-contain object-bottom transition-opacity duration-500"
                style={{ opacity: isHovered ? 1 : 0 }}
                draggable={false}
              />

              {/* Gold shimmer overlay on hover */}
              <div
                className="absolute inset-0 pointer-events-none transition-opacity duration-500"
                style={{
                  opacity: isHovered ? 1 : 0,
                  background: "linear-gradient(105deg, transparent 30%, rgba(255,211,114,0.15) 45%, rgba(255,240,200,0.25) 50%, rgba(255,211,114,0.15) 55%, transparent 70%)",
                  backgroundSize: "200% 100%",
                  animation: isHovered ? "goldShimmer 1.5s ease-in-out infinite" : "none",
                }}
              />

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#f0ead8] via-[#f0ead8]/30 to-transparent" />

              {/* Selected checkmark */}
              {isSelected && (
                <div className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-[#ffd372] border border-[#4c4c4c]">
                  <span className="text-[14px] font-bold text-[#2d2d2d]">✓</span>
                </div>
              )}

              {/* Styled by Hoku badge on hover */}
              {isHovered && !loading && (
                <div className="absolute right-3 top-3 rounded-full bg-[#ffd372] px-3 py-1 font-winky text-[11px] font-bold text-[#2d2d2d] shadow-sm border border-[#4c4c4c]">
                  Styled by Hoku ✨
                </div>
              )}

              {/* Name & tagline */}
              <div className="relative z-10 p-4">
                {loading && isSelected ? (
                  <p className="font-winky text-[14px] font-medium text-[#ffd372]">
                    {persona.loadingText}
                  </p>
                ) : (
                  <>
                    <p className="font-winky text-[20px] font-bold text-[#2d2d2d]">
                      {persona.name}
                    </p>
                    <p className="font-winky text-[12px] text-[#6b6b6b]">
                      {isHovered ? "✨ Now that's a look" : persona.tagline}
                    </p>
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <p className="px-5 pb-4 text-center font-winky text-[13px] text-red-400">
          {error}
        </p>
      )}
    </main>
  );
}
